import React, { useCallback, useEffect, useState } from 'react';
import { Card, Typography, Button, Space, Alert, List, Tag, Spin } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/layout/AppLayout';
import { usePaiementApi, type TEnfantAPayer } from '../../api/paiement/paiement';

const { Text, Title } = Typography;

/**
 * Retour après un paiement par carte.
 *
 * Le parcours quittait l'application pour la page hébergée du prestataire et
 * n'avait aucune route de retour : l'utilisateur atterrissait sur un écran qui
 * ne mentionnait pas son paiement, sans savoir s'il avait abouti.
 *
 * L'activation est asynchrone — c'est le webhook signé du prestataire qui
 * l'ouvre, jamais ce retour de navigateur. Cet écran ne fait donc que
 * CONSTATER : il relit les statuts et dit franchement quand c'est encore en
 * cours, plutôt que d'annoncer un succès qu'il ne peut pas garantir.
 */
const RetourPaiement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const paiementApi = usePaiementApi();

  const [enfants, setEnfants] = useState<TEnfantAPayer[]>([]);
  const [chargement, setChargement] = useState<boolean>(true);
  const [erreur, setErreur] = useState<boolean>(false);

  const recharger = useCallback(async () => {
    setChargement(true);
    setErreur(false);
    try {
      setEnfants(await paiementApi.listerMesEnfants());
    } catch {
      setErreur(true);
    } finally {
      setChargement(false);
    }
    // paiementApi n'a pas d'identité stable : le mettre en dépendance
    // relancerait l'appel à chaque rendu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void recharger();
  }, [recharger]);

  // Un paiement confirmé met quelques secondes à remonter : on relit une fois,
  // sans installer de boucle qui tournerait indéfiniment sur un échec.
  useEffect(() => {
    const enAttente = enfants.some((e) => e.statut === 'pending_payment');
    if (!enAttente) return;
    const t = setTimeout(() => void recharger(), 5000);
    return () => clearTimeout(t);
  }, [enfants, recharger]);

  const enAttente = enfants.some((e) => e.statut === 'pending_payment');
  const actifs = enfants.filter((e) => e.statut === 'active');

  return (
    <AppLayout title={t('payment.return.title', 'Retour de paiement')}>
      <Card style={{ maxWidth: 640, margin: '0 auto' }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {erreur ? (
            <Alert
              type="warning"
              showIcon
              message={t('payment.return.loadErrorTitle', 'État indisponible')}
              description={t(
                'payment.return.loadErrorHint',
                "Nous n'avons pas pu relire l'état de vos abonnements. Votre paiement n'est pas perdu pour autant : réessayez dans un instant."
              )}
            />
          ) : enAttente ? (
            <Alert
              type="info"
              showIcon
              icon={<ClockCircleOutlined />}
              message={t('payment.return.pendingTitle', 'Paiement en cours de confirmation')}
              description={t(
                'payment.return.pendingHint',
                "Nous attendons la confirmation de votre banque. Les comptes sont activés dès qu'elle nous parvient — quelques secondes en général."
              )}
            />
          ) : actifs.length > 0 ? (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message={t('payment.return.doneTitle', 'Abonnement actif')}
              description={t(
                'payment.return.doneHint',
                'Le paiement est confirmé et les comptes sont activés.'
              )}
            />
          ) : (
            <Alert
              type="info"
              showIcon
              message={t('payment.return.unknownTitle', 'Aucun abonnement actif pour le moment')}
              description={t(
                'payment.return.unknownHint',
                "Si vous venez de payer, la confirmation peut prendre quelques instants. Sinon, reprenez depuis la page Paiement."
              )}
            />
          )}

          <div>
            <Title level={5} style={{ marginBottom: 8 }}>
              {t('payment.return.childrenTitle', 'Vos comptes')}
            </Title>
            {chargement ? (
              <Spin />
            ) : (
              <List
                size="small"
                dataSource={enfants}
                locale={{ emptyText: t('payment.return.noChildren', 'Aucun compte rattaché.') }}
                renderItem={(e) => (
                  <List.Item
                    actions={[
                      e.statut === 'active' ? (
                        <Tag color="success" key="s">
                          {t('payment.return.statusActive', 'Actif')}
                          {e.date_fin ? ` · ${new Date(e.date_fin).toLocaleDateString('fr-FR')}` : ''}
                        </Tag>
                      ) : e.statut === 'pending_payment' ? (
                        <Tag color="processing" key="s">{t('payment.return.statusPending', 'En attente')}</Tag>
                      ) : (
                        <Tag key="s">{t('payment.return.statusNone', 'Non abonné')}</Tag>
                      ),
                    ]}
                  >
                    <Text>
                      {[e.prenom, e.nom].filter(Boolean).join(' ') || e.login || `#${e.userapp_id}`}
                    </Text>
                  </List.Item>
                )}
              />
            )}
          </div>

          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void recharger()} loading={chargement}>
              {t('payment.return.refresh', 'Actualiser')}
            </Button>
            <Button type="primary" onClick={() => navigate('/paiement')}>
              {t('payment.return.backToPayment', 'Voir mes abonnements')}
            </Button>
          </Space>
        </Space>
      </Card>
    </AppLayout>
  );
};

export default RetourPaiement;
